import { EventCard } from "./EventCard"
import { Calendar, LayoutGrid, List } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs"
import { CalendarView } from "../../landing/components/CalendarView"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"

interface Event {
  id: string
  title: string
  description: string
  imageUrl: string
  ecosystem: string
  type: 'hosted' | 'attending' | 'user' // Actualizado
  date?: string
  time?: string
  location: string | null
  category?: any
  eventType?: any
  modality?: any
  hostStatus?: 'pending' | 'accepted' | 'declined'
  sideEventsCount?: number
  parentEventId?: string | null
}

interface EventsListProps {
  events: Event[]
  viewMode: 'list' | 'calendar'
  onViewModeChange: (mode: 'list' | 'calendar') => void
  onManageEvent: (id: string) => void
  onShareEvent: (id: string) => void
  onEventClick?: (event: Event) => void
  hostedCount?: number
  attendingCount?: number
  userCount?: number // Nuevo
  onRefresh?: () => void
}

export const EventsList = ({ 
  events, 
  viewMode, 
  onViewModeChange, 
  onManageEvent, 
  onShareEvent,
  onEventClick,
  hostedCount = 0,
  attendingCount = 0,
  userCount = 0, // Nuevo
  onRefresh
}: EventsListProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const allowedFilters = new Set(["all", "hosted", "attending", "user-events", "side-events"])
  const urlFilter = searchParams.get('filter') || 'all'
  const initialFilter = allowedFilters.has(urlFilter) ? urlFilter : 'all'
  const [activeTab, setActiveTab] = useState<string>(initialFilter)

  useEffect(() => {
    const f = searchParams.get('filter') || 'all'
    const sanitized = allowedFilters.has(f) ? f : 'all'
    setActiveTab(sanitized)
  }, [searchParams])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    const next = new URLSearchParams(searchParams)
    next.set('filter', value)
    setSearchParams(next, { replace: true })
  }
  const hostedEvents = events.filter(event => event.type === 'hosted')
  const attendingEvents = events.filter(event => event.type === 'attending')
  const userEvents = events.filter(event => event.type === 'user') // Nuevo
  const sideEvents = events.filter(event => !!event.parentEventId)
  
  // Convert events to the format expected by CalendarView
  const formatEventsForCalendar = (eventsList: Event[]) => {
    return eventsList.map(event => ({
      id: event.id,
      title: event.title,
      description: event.description,
      image: event.imageUrl,
      category: event.category || event.ecosystem, // Usar category primero, luego ecosystem como fallback
      date: event.date || 'TBD',
      time: event.time || 'TBD',
      location: event.location || 'TBD',
      eventType: event.eventType,
      modality: event.modality
    }))
  }

  const handleEventClick = (event: any) => {
    if (onEventClick) {
      // Convert back to original format
      const originalEvent = events.find(e => e.id === event.id)
      if (originalEvent) {
        onEventClick(originalEvent)
      }
    }
  }

  const renderEventsList = (eventsList: Event[]) => {
    if (viewMode === 'calendar') {
      return (
        <CalendarView 
          events={formatEventsForCalendar(eventsList)} 
          onEventClick={handleEventClick}
        />
      )
    }

    return (
      <div className="space-y-4">
        {eventsList.map((event) => (
          <EventCard
            key={event.id}
            {...event}
            onManage={onManageEvent}
            onShare={onShareEvent}
            parentEventId={event.parentEventId}
            onLeft={onRefresh}
          />
        ))}
        {eventsList.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No se encontraron eventos.</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div className="relative overflow-x-auto">
            <TabsList className="bg-transparent p-0 h-auto rounded-none w-auto min-w-max">
              <TabsTrigger 
                value="all" 
                className="bg-transparent border-0 rounded-none px-2 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">Todos ({events.length})</span>
                <span className="sm:hidden">Todos</span>
              </TabsTrigger>
              <TabsTrigger 
                value="hosted"
                className="bg-transparent border-0 rounded-none px-2 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary text-sm whitespace-nowrap"
              >
                <span className="hidden md:inline">Organizados por mí ({hostedCount})</span>
                <span className="md:hidden">Organizados ({hostedCount})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="attending"
                className="bg-transparent border-0 rounded-none px-2 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">Ayudando ({attendingCount})</span>
                <span className="sm:hidden">Ayudando ({attendingCount})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="user-events"
                className="bg-transparent border-0 rounded-none px-2 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">Asistiendo ({userCount})</span>
                <span className="sm:hidden">Asist. ({userCount})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="side-events"
                className="bg-transparent border-0 rounded-none px-2 sm:px-4 py-2 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-primary text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">Eventos Paralelos ({sideEvents.length})</span>
                <span className="sm:hidden">Paralelos ({sideEvents.length})</span>
              </TabsTrigger>
            </TabsList>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200 -z-10"></div>
          </div>
          
          {/* View Mode Buttons - Hidden on mobile, visible on desktop */}
          <div className="hidden md:flex gap-2 justify-center sm:justify-end">
            <Button 
              variant={viewMode === 'list' ? 'outline-selected' : 'outline'}
              size="sm"
              onClick={() => onViewModeChange('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'calendar' ? 'outline-selected' : 'outline'}
              size="sm"
              onClick={() => onViewModeChange('calendar')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <TabsContent value="all">
          {renderEventsList(events)}
        </TabsContent>

        <TabsContent value="hosted">
          {hostedEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Aún no organizaste eventos.</p>
              <p className="text-sm mt-2">¡Creá tu primer evento para comenzar!</p>
            </div>
          ) : (
            renderEventsList(hostedEvents)
          )}
        </TabsContent>

        <TabsContent value="attending">
          {attendingEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Aún no estás ayudando en ningún evento.</p>
              <p className="text-sm mt-2">Explorá eventos para encontrar los que te interesen.</p>
            </div>
          ) : (
            renderEventsList(attendingEvents)
          )}
        </TabsContent>

        <TabsContent value="user-events">
          {userEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Aún no estás asistiendo a ningún evento.</p>
              <p className="text-sm mt-2">Explorá eventos para encontrar los que te interesen.</p>
            </div>
          ) : (
            renderEventsList(userEvents)
          )}
        </TabsContent>

        <TabsContent value="side-events">
          {sideEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No se encontraron eventos paralelos.</p>
            </div>
          ) : (
            renderEventsList(sideEvents)
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}